import { useSyncExternalStore } from 'react';
import { yomiriP2P, type P2PState } from '../../cast/YomiriP2PBridge.ts';
import type { CastCommand } from '../../cast/YomiriCastChannel.ts';

export type TvSyncState =
  | 'desconectado'
  | 'conectando'
  | 'conectado'
  | 'enviando'
  | 'recibido'
  | 'reproduciendo';

export interface TvSyncStatus {
  state: TvSyncState;
  peerName?: string;
  lastMessage?: string;
  lastUpdated: number;
}

export const EVENT_TV_SYNC_CHANGED = 'yomiri-tv-sync-changed';
export const EVENT_PLAYBACK_EFFECTIVE = 'yomiri-playback-started';

export class TvPairingSyncManager {
  private static instance: TvPairingSyncManager;

  private currentStatus: TvSyncStatus = {
    state: 'desconectado',
    lastUpdated: Date.now(),
  };

  private listeners = new Set<(status: TvSyncStatus) => void>();

  public static getInstance(): TvPairingSyncManager {
    if (!TvPairingSyncManager.instance) {
      TvPairingSyncManager.instance = new TvPairingSyncManager();
    }
    return TvPairingSyncManager.instance;
  }

  private constructor() {
    this.init();
  }

  private init() {
    // Monitor P2P bridge connection state
    yomiriP2P.onState((p2pState: P2PState, peerName?: string) => {
      if (p2pState === 'disconnected') {
        this.setStatus('desconectado', peerName, yomiriP2P.getError() || 'Dispositivo desconectado');
      } else if (p2pState === 'connecting') {
        this.setStatus('conectando', peerName, 'Conectando con la TV…');
      } else if (p2pState === 'connected') {
        this.setStatus('conectado', peerName, `Conectado a ${peerName || 'la TV'}`);
      }
    });

    // Monitor P2P commands for feedback and separate confirmation of reception and playback
    yomiriP2P.onCommand((cmd: CastCommand) => {
      if (cmd.type === 'PLAYBACK_STATUS') {
        if (cmd.status === 'recibido') {
          this.setStatus('recibido', undefined, cmd.message || 'Contenido recibido en la TV.');
        } else if (cmd.status === 'reproduciendo') {
          this.setStatus('reproduciendo', undefined, cmd.message || 'Reproduciendo en la TV.');
        }
      } else if (cmd.type === 'TV_FEEDBACK') {
        if (cmd.stage === 'recibido' || cmd.message.toLowerCase().includes('recibi')) {
          this.setStatus('recibido', undefined, cmd.message);
        } else if (cmd.stage === 'reproduciendo' || cmd.message.toLowerCase().includes('reproducien')) {
          this.setStatus('reproduciendo', undefined, cmd.message);
        }
      }
    });

    // Listen to local playback events (when running on TV)
    if (typeof window !== 'undefined') {
      window.addEventListener(EVENT_PLAYBACK_EFFECTIVE, (e: any) => {
        if (yomiriP2P.getRole() === 'tv' && yomiriP2P.getState() === 'connected') {
          this.confirmPlayback(e?.detail?.message);
        }
      });
    }
  }

  public getStatus(): TvSyncStatus {
    return this.currentStatus;
  }

  public setStatus(state: TvSyncState, peerName?: string, message?: string) {
    this.currentStatus = {
      state,
      peerName: peerName ?? this.currentStatus.peerName,
      lastMessage: message ?? this.currentStatus.lastMessage,
      lastUpdated: Date.now(),
    };
    this.notify();
  }

  private notify() {
    for (const listener of this.listeners) {
      try {
        listener(this.currentStatus);
      } catch (err) {
        console.error('[TvPairingSync] Listener error:', err);
      }
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(EVENT_TV_SYNC_CHANGED, { detail: this.currentStatus }));
    }
  }

  public subscribe(listener: (status: TvSyncStatus) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Called by remote when dispatching a command to TV.
   * State transitions to 'enviando'.
   */
  public markSending(message = 'Enviando a la TV…') {
    if (yomiriP2P.getState() === 'connected') {
      this.setStatus('enviando', undefined, message);
    }
  }

  /**
   * Called on TV to confirm reception of a command/route from mobile.
   */
  public confirmReception(message = 'Contenido recibido en la TV.') {
    this.setStatus('recibido', undefined, message);
    if (yomiriP2P.getRole() === 'tv') {
      yomiriP2P.send({
        type: 'PLAYBACK_STATUS',
        status: 'recibido',
        message,
      });
      yomiriP2P.send({
        type: 'TV_FEEDBACK',
        message,
        stage: 'recibido',
      });
    }
  }

  /**
   * Called on TV when playback effectively begins.
   */
  public confirmPlayback(message = 'Reproduciendo en la TV.') {
    this.setStatus('reproduciendo', undefined, message);
    if (yomiriP2P.getRole() === 'tv') {
      yomiriP2P.send({
        type: 'PLAYBACK_STATUS',
        status: 'reproduciendo',
        message,
      });
      yomiriP2P.send({
        type: 'TV_FEEDBACK',
        message,
        stage: 'reproduciendo',
      });
    }
  }
}

export const tvPairingSync = TvPairingSyncManager.getInstance();

/**
 * Notifies the TV system that media playback or reading has effectively started.
 */
export function notifyPlaybackEffective(message?: string) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT_PLAYBACK_EFFECTIVE, { detail: { message } }));
  }
}

/**
 * Hook for components to reactively observe mobile/TV pairing lifecycle states:
 * desconectado → conectando → conectado → enviando → recibido → reproduciendo
 */
export function useTvPairingSync(): TvSyncStatus & {
  markSending: (msg?: string) => void;
  confirmReception: (msg?: string) => void;
  confirmPlayback: (msg?: string) => void;
} {
  const status = useSyncExternalStore(
    (cb) => tvPairingSync.subscribe(cb),
    () => tvPairingSync.getStatus(),
    () => tvPairingSync.getStatus()
  );

  return {
    ...status,
    markSending: tvPairingSync.markSending.bind(tvPairingSync),
    confirmReception: tvPairingSync.confirmReception.bind(tvPairingSync),
    confirmPlayback: tvPairingSync.confirmPlayback.bind(tvPairingSync),
  };
}
